/**
 * OffreShell O1 — routage UX création (Admin only).
 * Pas de fusion Prisma : 1 choix commerçant → 1 destination technique unique.
 */

export const OFFRE_KIND = Object.freeze({
  PRODUIT: 'PRODUIT',
  PRESTATION: 'PRESTATION',
});

export const PRESTATION_MODE = Object.freeze({
  RESERVABLE: 'RESERVABLE',
  SANS_RESERVATION: 'SANS_RESERVATION',
});

/** Destinations techniques (jamais exposées telles quelles au commerçant). */
export const OFFRE_DESTINATION = Object.freeze({
  PRODUIT_PHYSIQUE: 'PRODUIT_PHYSIQUE',
  /** Prestation sans RDV client → modèle Prestation + reservationEnLigne=false. */
  PRESTATION_SANS_RESERVATION: 'PRESTATION_SANS_RESERVATION',
  /** Prestation réservable → modèle Prestation + reservationEnLigne=true. */
  PRESTATION_RESERVABLE: 'PRESTATION_RESERVABLE',
});

/**
 * @param {{ kind?: string|null, prestationMode?: string|null }} input
 * @returns {string|null} OFFRE_DESTINATION ou null si incomplete
 */
export function resolveOffreCreateDestination({ kind, prestationMode } = {}) {
  if (kind === OFFRE_KIND.PRODUIT) {
    return OFFRE_DESTINATION.PRODUIT_PHYSIQUE;
  }
  if (kind === OFFRE_KIND.PRESTATION) {
    if (prestationMode === PRESTATION_MODE.SANS_RESERVATION) {
      return OFFRE_DESTINATION.PRESTATION_SANS_RESERVATION;
    }
    if (prestationMode === PRESTATION_MODE.RESERVABLE) {
      return OFFRE_DESTINATION.PRESTATION_RESERVABLE;
    }
    return null;
  }
  return null;
}

/**
 * Map destination catalogue → typeProduit Prisma (null si hors ProduitModal).
 * @param {string|null} destination
 * @returns {'PHYSIQUE'|'SERVICE'|null}
 */
export function destinationToTypeProduit(destination) {
  if (destination === OFFRE_DESTINATION.PRODUIT_PHYSIQUE) return 'PHYSIQUE';
  // Prestations (avec ou sans réservation) → table Prestation, pas Produit SERVICE.
  return null;
}

/**
 * Une offre = une destination. Jamais Produit + Prestation ensemble.
 * @param {string|null} destination
 * @returns {{ ok: boolean, createsProduit: boolean, createsPrestation: boolean }}
 */
export function describeOffreCreateTargets(destination) {
  if (destination === OFFRE_DESTINATION.PRODUIT_PHYSIQUE) {
    return { ok: true, createsProduit: true, createsPrestation: false };
  }
  if (destination === OFFRE_DESTINATION.PRESTATION_SANS_RESERVATION) {
    return { ok: true, createsProduit: false, createsPrestation: true };
  }
  if (destination === OFFRE_DESTINATION.PRESTATION_RESERVABLE) {
    return { ok: true, createsProduit: false, createsPrestation: true };
  }
  return { ok: false, createsProduit: false, createsPrestation: false };
}

/**
 * Contrat O1 — branche « Réservable en ligne ».
 * Doit réutiliser le flow Mes Prestations → Ajouter (openCreatePrestation → handleAddPrestation).
 * Aucun nouveau payload Prestation, aucun ProduitModal / Service Form.
 */
export const OFFRE_RESERVABLE_BRIDGE = Object.freeze({
  destination: OFFRE_DESTINATION.PRESTATION_RESERVABLE,
  /** Prop ProduitsManager → CommercantDetail */
  callbackProp: 'onRequestPrestationReservable',
  /** Même entrée que Mes Prestations / « Ajouter un élément » prestation */
  creationFlowArg: 'prestation',
  /** Imperative handle ServicesManager — alias exact de handleAddPrestation */
  servicesManagerMethod: 'openCreatePrestation',
});

/**
 * Garde-fou : réservable ≠ catalogue Produit.
 * @param {string|null} destination
 */
export function isOffreReservableBridgeOnly(destination) {
  return (
    destination === OFFRE_DESTINATION.PRESTATION_RESERVABLE &&
    destinationToTypeProduit(destination) === null &&
    describeOffreCreateTargets(destination).createsProduit === false &&
    describeOffreCreateTargets(destination).createsPrestation === true
  );
}

/**
 * Libellé type verrouillé (édition / en-tête modal) — vocabulaire commerçant.
 * @param {{ isService?: boolean, isPrestationReservable?: boolean }} opts
 */
export function getOffreLockedTypeLabel({
  isService = false,
  isPrestationReservable = false,
} = {}) {
  if (isPrestationReservable) {
    return 'Type : Prestation — Réservable en ligne';
  }
  if (isService) {
    return 'Type : Prestation — Sans réservation en ligne';
  }
  return 'Type : Produit';
}
